import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Copy, Search, Loader2, Filter } from "lucide-react";
import { api, formatApiError, resolveMediaUrl } from "@/lib/api";

const AdminMedia = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [q, setQ] = useState("");
  const [folder, setFolder] = useState("");
  const inputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/media", { params: folder ? { folder } : {} });
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [folder]);

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let ok = 0, fail = 0;
    for (const file of files) {
      if (file.size > 8 * 1024 * 1024) { toast.error(`${file.name} > 8 MB, dilewati`); fail++; continue; }
      const fd = new FormData();
      fd.append("file", file);
      try {
        await api.post(`/media/upload`, fd, {
          params: { folder: folder || "umum" },
          headers: { "Content-Type": "multipart/form-data" },
        });
        ok++;
      } catch (e) {
        toast.error(`${file.name}: ${formatApiError(e.response?.data?.detail) || "gagal"}`);
        fail++;
      }
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
    if (ok) toast.success(`${ok} file berhasil diunggah`);
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Hapus media ini?")) return;
    await api.delete(`/media/${id}`);
    toast.success("Terhapus");
    load();
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("URL disalin");
  };

  const filtered = items.filter((it) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (it.original_filename || "").toLowerCase().includes(s) || (it.folder || "").toLowerCase().includes(s);
  });

  const folders = Array.from(new Set(items.map((i) => i.folder))).filter(Boolean);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <div>
          <h1 className="text-3xl font-display font-bold">Media Manager</h1>
          <p className="mt-1 text-sm text-zinc-500">Kelola semua file & gambar. Otomatis dikompres ke WebP.</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => upload(e.target.files)}
          />
          <button
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid="media-upload-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-maroon-800 text-white font-semibold hover:bg-maroon-900 disabled:opacity-60"
          >
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            Upload File
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari file..."
            className="w-full pl-10 pr-4 py-2.5 rounded-full border border-zinc-200 focus:border-maroon-700 outline-none text-sm"
          />
        </div>
        <div className="flex items-center gap-2 bg-white rounded-full px-3 py-1 border border-zinc-200">
          <Filter size={14} className="text-zinc-500" />
          <select
            value={folder}
            onChange={(e) => setFolder(e.target.value)}
            className="text-sm outline-none bg-transparent"
          >
            <option value="">Semua folder</option>
            {folders.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-zinc-500 text-sm">Memuat...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-zinc-500 text-sm">Belum ada media. Upload untuk memulai.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {filtered.map((it) => (
            <div key={it.id} className="group relative bg-white rounded-2xl border border-zinc-200 overflow-hidden">
              <div className="aspect-square bg-zinc-50">
                <img src={resolveMediaUrl(it.url)} alt={it.original_filename} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-3">
                <div className="text-xs font-semibold text-zinc-900 truncate" title={it.original_filename}>{it.original_filename}</div>
                <div className="text-[10px] text-zinc-500 flex items-center justify-between mt-1">
                  <span>{it.folder}</span>
                  <span>{(it.size / 1024).toFixed(0)} KB</span>
                </div>
              </div>
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2">
                <button onClick={() => copyUrl(it.url)} className="w-10 h-10 rounded-full bg-white text-zinc-800 flex items-center justify-center hover:bg-maroon-500 hover:text-white transition" title="Salin URL">
                  <Copy size={14} />
                </button>
                <button onClick={() => del(it.id)} data-testid={`media-delete-${it.id}`} className="w-10 h-10 rounded-full bg-white text-red-600 flex items-center justify-center hover:bg-red-500 hover:text-white transition" title="Hapus">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminMedia;
